// Matchday setup: start a match and hand the scorer a QR code.
import qrcode from 'qrcode-generator';
import { totals, phaseLabel, parseTeamText, type MatchState } from '../shared/match';
import type { NewMatchReply } from '../shared/protocol';
import { DEMO_CLUBS, type Opponent } from '../shared/club';
import { getRules } from '../shared/rules';
import { mountHelp } from './help';
import { loadClub, esc, param, type Club, venueParam, hostVenue, brandHeader } from './net';
import { prepareCrest, uploadImage } from './imaging-browser';

const venue = venueParam();
const app = document.getElementById('app') as HTMLElement;
let club: Club | null = null;
let code = '';
let oppCrest: string | null = null;     // uploaded opponent crest for the match being set up
try { code = localStorage.getItem(`ml-admin-${venue}`) ?? ''; } catch { /* storage blocked */ }
if (param('venue')) document.querySelectorAll<HTMLAnchorElement>('header nav a').forEach(a => { const u = new URL(a.href); u.searchParams.set('venue', venue); a.href = u.href; });

const api = (path: string, init: RequestInit = {}) =>
  fetch(`/api/venue/${venue}/${path}`, { ...init, headers: { 'content-type': 'application/json', 'x-admin-code': encodeURIComponent(code), ...(init.headers ?? {}) } });

function qr(text: string) {
  const q = qrcode(0, 'M'); q.addData(text); q.make();
  return q.createSvgTag({ cellSize: 6, margin: 2, scalable: true });
}

function signIn(msg = '') {
  app.innerHTML = `<section class="card narrow"><h2>Matchday setup</h2>
    <p>Enter the club's admin code to start a match.</p>
    <form id="login"><label class="field">Admin code<input id="code" type="password" autocomplete="current-password" required></label>
    ${msg ? `<p class="err">${esc(msg)}</p>` : ''}<button class="go">Continue</button></form></section>`;
  (document.getElementById('login') as HTMLFormElement).onsubmit = async e => {
    e.preventDefault();
    code = (document.getElementById('code') as HTMLInputElement).value.trim();
    await show();
  };
}

const crestPreview = (url: string | null) => url ? `<img src="${esc(url)}" alt="Opponent crest">` : '<span>No crest</span>';

async function show() {
  const r = await api('admin');
  if (r.status === 401) return signIn(code ? 'That admin code is not right.' : '');
  try { localStorage.setItem(`ml-admin-${venue}`, code); } catch { /* fine */ }
  const { pin, state, opponents = [] } = await r.json() as { pin: string | null; state: MatchState | null; opponents?: Opponent[] };
  const squads = Object.entries(club?.squads ?? {});
  oppCrest = null;
  // one opponent list: the league from Studio first, then anyone else played before (cup sides)
  const league = club?.league ?? [];
  const played = opponents.filter(o => !league.some(l => l.name.toLowerCase() === o.name.toLowerCase()));
  const picks: Opponent[] = [...league, ...played];

  if (state) return showLive(state, pin);

  app.innerHTML = `
    <section class="card"><h2>Start a match</h2>
      <form id="new" class="grid">
        <label class="field">Our team<select id="squad">${[...squads].sort((a, b) => Number(!a[1].players.length) - Number(!b[1].players.length)).map(([id, s]) => `<option value="${esc(id)}">${esc(club!.teamName)} ${esc(s.label)}${s.players.length ? '' : ' (no players yet)'}</option>`).join('')}</select></label>
        ${picks.length ? `<label class="field wide">Opponent<select id="prevOpp"><option value="">Type in a new opponent…</option>
          ${league.length ? `<optgroup label="League">${league.map(o => `<option value="${picks.indexOf(o)}">${esc(o.name)}</option>`).join('')}</optgroup>` : ''}
          ${played.length ? `<optgroup label="Played before">${played.map(o => `<option value="${picks.indexOf(o)}">${esc(o.name)}</option>`).join('')}</optgroup>` : ''}
          </select></label>` : ''}
        <fieldset class="field wide matchtype" ${getRules(club?.sport ?? '').bonus ? '' : 'hidden'}><legend>Match type</legend>
          <label><input type="radio" name="mtype" value="league" checked> League match</label>
          <label><input type="radio" name="mtype" value="cup"> Cup or friendly</label>
          <span class="note">League matches show bonus points at full time; cup matches and friendlies don't.</span></fieldset>
        <label class="field">Opponent name<input id="oppName" required maxlength="24" placeholder="e.g. Harrogate"></label>
        <label class="field">Opponent short name<input id="oppShort" maxlength="3" placeholder="HAR"></label>
        <label class="field">Opponent colour<input id="oppColor" type="color" value="#1d4ed8"></label>
        <div class="field crestfield">Opponent crest
          <div class="crestrow"><div class="crestprev" id="oppCrestPrev">${crestPreview(null)}</div>
            <div class="creststack"><label class="filebtn">Upload crest<input type="file" id="oppCrestFile" accept="image/*"></label>
              <label class="check small"><input type="checkbox" id="oppCrestCut" checked> Remove white around it</label>
              <button type="button" class="small quiet" id="oppCrestClear" hidden>Use no crest</button></div></div></div>
        ${club?.sponsors.length ? `<fieldset class="field wide sponsorpick"><legend>Sponsors for this match</legend>
          <p class="note">Only ticked sponsors appear on the screen. Match day sponsors take every other slot, labelled "Today's match day sponsor".</p>
          <div class="sphead" aria-hidden="true"><span></span><span>Display for this match</span><span>Match day sponsor</span></div>
          ${club.sponsors.map((sp, i) => `<div class="sprow">
            <span class="spname"><span class="splogo" style="background:${esc(sp.bg)}"><img src="${esc(sp.logo)}" alt=""></span>${esc(sp.name)}</span>
            <label class="spbox"><input type="checkbox" data-show="${i}" checked><span class="sr">Display ${esc(sp.name)} for this match</span></label>
            <label class="spbox"><input type="checkbox" data-md="${i}"><span class="sr">${esc(sp.name)} is a match day sponsor</span></label></div>`).join('')}
          <p class="sptools"><button type="button" class="small quiet" id="spAll">Tick all</button> <button type="button" class="small quiet" id="spNone">Untick all</button> <span class="note" id="spCount"></span></p>
        </fieldset>` : ''}
        <p class="err" id="startErr" hidden></p>
        <button class="go" id="startBtn">Start match</button>
      </form></section>${demoCard()}`;
  wireDemo();

  const $ = (id: string) => document.getElementById(id) as HTMLInputElement;
  const setCrest = (url: string | null) => {
    oppCrest = url;
    document.getElementById('oppCrestPrev')!.innerHTML = crestPreview(url);
    document.getElementById('oppCrestClear')!.hidden = !url;
  };
  document.getElementById('prevOpp')?.addEventListener('change', e => {
    const val = (e.target as HTMLSelectElement).value;
    const o = val === '' ? undefined : picks[Number(val)];   // "Type in a new opponent…" has no value (Number('') would be 0, the first club)
    // a club from the league list is a league match; anyone else is most likely a cup tie or friendly (it can be changed)
    const inLeague = !!o && league.includes(o);
    (document.querySelector<HTMLInputElement>(`input[name="mtype"][value="${inLeague ? 'league' : 'cup'}"]`)!).checked = true;
    if (!o) { $('oppName').value = ''; $('oppShort').value = ''; $('oppColor').value = '#1d4ed8'; setCrest(null); return; }
    $('oppName').value = o.name; $('oppShort').value = o.short; $('oppColor').value = o.color; setCrest(o.crest);
  });
  $('oppCrestFile').addEventListener('change', async e => {
    const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
    const prev = document.getElementById('oppCrestPrev')!;
    prev.innerHTML = '<span>Preparing…</span>';
    try { setCrest(await uploadImage(venue, code, await prepareCrest(f, $('oppCrestCut').checked))); }
    catch (err) { setCrest(oppCrest); showErr((err as Error).message); }
    (e.target as HTMLInputElement).value = '';
  });
  document.getElementById('oppCrestClear')!.onclick = () => setCrest(null);
  // a match day sponsor is always displayed; a sponsor not displayed can't be the match day sponsor
  const boxes = (kind: 'show' | 'md') => [...app.querySelectorAll<HTMLInputElement>(`input[data-${kind}]`)];
  const count = () => {
    const shown = boxes('show').filter(b => b.checked).length, md = boxes('md').filter(b => b.checked).length, el = document.getElementById('spCount');
    if (el) el.textContent = shown ? `${shown} of ${boxes('show').length} sponsors on the screen${md ? `, ${md} as match day sponsor${md > 1 ? 's' : ''}` : ''}` : 'No sponsors will be shown this match';
  };
  app.querySelectorAll<HTMLInputElement>('input[data-md]').forEach(b => b.addEventListener('change', () => {
    if (b.checked) app.querySelector<HTMLInputElement>(`input[data-show="${b.dataset.md}"]`)!.checked = true; count();
  }));
  app.querySelectorAll<HTMLInputElement>('input[data-show]').forEach(b => b.addEventListener('change', () => {
    if (!b.checked) app.querySelector<HTMLInputElement>(`input[data-md="${b.dataset.show}"]`)!.checked = false; count();
  }));
  document.getElementById('spAll')?.addEventListener('click', () => { boxes('show').forEach(b => b.checked = true); count(); });
  document.getElementById('spNone')?.addEventListener('click', () => { [...boxes('show'), ...boxes('md')].forEach(b => b.checked = false); count(); });
  count();

  (document.getElementById('new') as HTMLFormElement).onsubmit = async e => {
    e.preventDefault();
    const v = (id: string) => $(id).value.trim();
    const body = {
      rulesId: club?.sport ?? 'rugby-union',
      squad: v('squad'),
      home: { name: club?.teamName ?? 'Home', short: club?.short ?? 'HOM', color: club?.color ?? '#f5b400', crest: club?.crest || null },
      away: { name: v('oppName'), short: v('oppShort') || v('oppName'), color: v('oppColor'), crest: oppCrest },
      league: document.querySelector<HTMLInputElement>('input[name="mtype"]:checked')?.value !== 'cup',
      sponsors: (club?.sponsors ?? []).flatMap((sp, i) => app.querySelector<HTMLInputElement>(`input[data-show="${i}"]`)?.checked
        ? [{ ...sp, matchday: !!app.querySelector<HTMLInputElement>(`input[data-md="${i}"]`)?.checked }] : []),
    };
    const btn = document.getElementById('startBtn') as HTMLButtonElement;
    btn.disabled = true; btn.textContent = 'Starting…';
    const res = await api('match', { method: 'POST', body: JSON.stringify(body) });
    const out = await res.json() as NewMatchReply & { error?: string };
    if (!res.ok) { btn.disabled = false; btn.textContent = 'Start match'; showErr(out.error ?? 'Could not start the match'); return; }
    await show();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
}

// ---- a match is on: show it, how the scorer gets in, and a way to end it ----
let liveTimer = 0;
/** Which sponsors this match shows, for the in-progress card. */
function sponsorNote(s: MatchState) {
  if (!s.sponsors) return s.matchSponsor ? `<p class="note">Matchday sponsor: <b>${esc(s.matchSponsor.name)}</b></p>` : '';
  if (!s.sponsors.length) return '<p class="note">No sponsors on the screen this match.</p>';
  const md = s.sponsors.filter(x => x.matchday), rest = s.sponsors.filter(x => !x.matchday);
  return `<p class="note">${md.length ? `Match day sponsor${md.length > 1 ? 's' : ''}: <b>${md.map(x => esc(x.name)).join(', ')}</b>. ` : ''}${rest.length ? `Also on the screen: ${rest.map(x => esc(x.name)).join(', ')}.` : ''}</p>`;
}

// ---- today's teams: our players ticked with today's shirt numbers, the opponents typed in ----
function squadOf(state: MatchState) {
  const sq = (state.squad && club?.squads[state.squad]) || Object.values(club?.squads ?? {})[0];
  return sq ? [...sq.players].sort((a, b) => (Number(a.number) || 999) - (Number(b.number) || 999) || a.name.localeCompare(b.name)) : [];
}
/** The name of the team playing, as Studio has it ("Men's XV"). */
const squadLabel = (state: MatchState) => (state.squad && club?.squads[state.squad]?.label) || '';
function teamsCard(state: MatchState) {
  const players = squadOf(state), today = new Map((state.lineups?.home ?? []).map(t => [t.player, t.number]));
  const awayText = (state.lineups?.away ?? []).map(r => `${r.number ? `${r.number} ` : ''}${r.name}`).join('\n');
  return `<section class="card lineup" id="teamsCard"><h2>Today's teams</h2>
    <p class="note">The screen shows them before kick-off, taking turns with the scoreboard, and whenever the scorer chooses Teams. The scorer's "Who scored?" list shows today's team.</p>
    <div class="lu-cols">
      <div><h3>${esc(state.teams.home.name)}${squadLabel(state) ? ` <span class="sqlabel">· ${esc(squadLabel(state))}</span>` : ''}</h3>
        ${players.length ? `<div class="lu-list">${players.map(p => `<div class="lu-row">
            <input type="checkbox" data-lu="${esc(p.id)}" ${today.has(p.id) ? 'checked' : ''} aria-label="${esc(p.name || 'Player')} is in today's team">
            <input class="lu-no" data-luno="${esc(p.id)}" inputmode="numeric" maxlength="3" value="${esc(today.get(p.id) ?? '')}" placeholder="${esc(p.number || 'No.')}" aria-label="Shirt number today">
            ${p.photo ? `<img src="${esc(p.photo)}" alt="">` : '<i></i>'}<span>${esc(p.name || 'No name yet')}</span></div>`).join('')}</div>
          <p class="note">Tick who's playing and give their shirt number today (${(() => { const r = getRules(state.rulesId); return `1–${r.starters} start, ${r.starters + 1} and up are ${r.words.bench.toLowerCase()}`; })()}).</p>
          <p class="lu-tools"><button type="button" class="small quiet" id="luClear">Untick all</button> <span class="note" id="luCount"></span></p>`
        : `<p class="note">${esc(squadLabel(state) || 'This team')} has no players in Studio yet, so there's no one to tick. Add them in Studio (Teams and players) and they appear here. If this match should be for a different team, end it and start a new one, choosing that team as "Our team".</p>`}
      </div>
      <div><h3>${esc(state.teams.away.name)}</h3>
        <textarea id="awayTeam" rows="16" spellcheck="false" placeholder="1 John Smith&#10;2 Tom Brown&#10;3 …">${esc(awayText)}</textarea>
        <p class="note">One player per line, shirt number first. Lines without a number are numbered in order.</p>
      </div>
    </div>
    <div class="lu-save"><button class="go" id="saveTeams">Save today's teams</button> <span id="teamsMsg" class="note" role="status"></span></div>
  </section>`;
}
function wireTeams(state: MatchState) {
  const card = document.getElementById('teamsCard'); if (!card) return;
  const ticks = [...card.querySelectorAll<HTMLInputElement>('input[data-lu]')];
  const noOf = (id: string) => card.querySelector<HTMLInputElement>(`input[data-luno="${CSS.escape(id)}"]`)!;
  const count = () => { const n = ticks.filter(t => t.checked).length, el = document.getElementById('luCount'); if (el) el.textContent = `${n} ticked`; };
  ticks.forEach(t => t.addEventListener('change', () => {
    const no = noOf(t.dataset.lu!);
    // ticking someone fills in their usual number (or the next free one); a typed number ticks them
    if (t.checked && !no.value) {
      const used = new Set(ticks.filter(x => x.checked && x !== t).map(x => noOf(x.dataset.lu!).value));
      let n = no.placeholder !== 'No.' && !used.has(no.placeholder) ? Number(no.placeholder) : 1;
      while (used.has(String(n))) n++;
      no.value = String(n);
    }
    count();
  }));
  card.querySelectorAll<HTMLInputElement>('input[data-luno]').forEach(no => no.addEventListener('input', () => {
    no.value = no.value.replace(/\D/g, '');
    const t = ticks.find(x => x.dataset.lu === no.dataset.luno)!; if (no.value && !t.checked) { t.checked = true; count(); }
  }));
  document.getElementById('luClear')?.addEventListener('click', () => { ticks.forEach(t => { t.checked = false; noOf(t.dataset.lu!).value = ''; }); count(); });
  count();
  document.getElementById('saveTeams')!.onclick = async () => {
    const btn = document.getElementById('saveTeams') as HTMLButtonElement, msg = document.getElementById('teamsMsg')!;
    const home = ticks.filter(t => t.checked).map(t => ({ player: t.dataset.lu!, number: noOf(t.dataset.lu!).value }));
    const nums = home.map(h => h.number).filter(Boolean);
    if (new Set(nums).size !== nums.length) { msg.textContent = 'Two of our players have the same shirt number. Change one and save again.'; msg.className = 'err'; return; }
    const away = parseTeamText((document.getElementById('awayTeam') as HTMLTextAreaElement).value);
    btn.disabled = true; btn.textContent = 'Saving…';
    const r = await api('lineup', { method: 'POST', body: JSON.stringify({ home, away }) });
    btn.disabled = false; btn.textContent = "Save today's teams";
    const out = await r.json().catch(() => ({})) as { error?: string };
    if (!r.ok) { msg.textContent = out.error ?? 'Could not save the teams. Try again.'; msg.className = 'err'; return; }
    msg.className = 'note ok'; msg.textContent = `Saved: ${home.length} ${state.teams.home.name} and ${away.length} ${state.teams.away.name} players. The screen has them now.`;
  };
}

function showLive(state: MatchState, pin: string | null) {
  const scorerUrl = `${location.origin}/pitchside.html?venue=${venue}&pin=${pin}`;
  const shortUrl = `${location.host}/score${venue === (hostVenue() ?? 'wensleydale') ? '' : `?venue=${encodeURIComponent(venue)}`}`;
  const stageUrl = `${location.origin}/stage.html?venue=${venue}`;
  const ft = state.phase === 'FT';
  const line = (s: MatchState) => { const t = totals(s); return `${esc(s.teams.home.name)} <b>${t.home} – ${t.away}</b> ${esc(s.teams.away.name)} · ${esc(phaseLabel(s))}`; };
  app.innerHTML = `<section class="card current"><h2>${ft ? 'Full-time: final score on the screen' : 'A match is in progress'}</h2>
      <p class="match" id="liveLine">${line(state)}</p>
      ${sponsorNote(state)}
      <div class="handover"><div class="qr" aria-label="QR code for the scorer's link">${qr(scorerUrl)}</div>
        <div class="steps"><p><b>Scorer:</b> scan this with your phone camera.</p>
          <p class="pin">PIN ${esc(pin)}</p>
          <p class="rejoin">Closed the scoring page? On any phone go to <b id="shortUrl">${esc(shortUrl)}</b> <button class="small" data-copy="shortUrl">Copy</button> and enter the PIN. A phone that has scored before goes straight back in.</p>
          <p class="link"><span id="scorerUrl">${esc(scorerUrl)}</span> <button class="small" data-copy="scorerUrl">Copy full link</button></p></div></div>
      <p class="note">Stage box address (set once): <span id="stageUrl">${esc(stageUrl)}</span> <button class="small" data-copy="stageUrl">Copy</button></p>
    </section>
    ${teamsCard(state)}
    <section class="card endcard" id="endBox">
      <h2>Finished with this match?</h2>
      <p>Ending it puts the club crest and sponsors back on the screen and switches off the scorer's link. Then you can set up the next match.</p>
      <div><button class="danger" id="endBtn">End this match</button></div>
    </section>${demoCard()}`;
  wireDemo();
  app.querySelectorAll<HTMLButtonElement>('[data-copy]').forEach(b => b.onclick = async () => {
    const el = document.getElementById(b.dataset.copy!)!;
    try { await navigator.clipboard.writeText(el.textContent ?? ''); b.textContent = 'Copied'; }
    catch { getSelection()?.selectAllChildren(el); b.textContent = 'Selected'; }
  });
  wireTeams(state);
  const box = document.getElementById('endBox')!;
  document.getElementById('endBtn')!.onclick = () => {
    box.innerHTML = `<h2>End ${esc(state.teams.home.name)} v ${esc(state.teams.away.name)}?</h2>
      <p>${ft ? 'The final score leaves the screen.' : `The match is not at full-time yet (${esc(phaseLabel(state))}).`} The scorer's link stops working straight away.</p>
      <div class="pairrow"><button class="danger" id="endYes">Yes, end the match</button><button id="endNo">Keep it going</button></div>
      <p class="err" id="endErr" hidden></p>`;
    document.getElementById('endNo')!.onclick = () => showLive(state, pin);
    document.getElementById('endYes')!.onclick = async () => {
      const b = document.getElementById('endYes') as HTMLButtonElement;
      b.disabled = true; b.textContent = 'Ending…';
      const r = await api('end', { method: 'POST' });
      if (!r.ok) { const e = document.getElementById('endErr')!; e.textContent = (await r.json().catch(() => ({}))).error ?? 'Could not end the match'; e.hidden = false; b.disabled = false; b.textContent = 'Yes, end the match'; return; }
      clearInterval(liveTimer);
      await show();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
  };
  // keep the score line current while the page is open
  clearInterval(liveTimer);
  liveTimer = window.setInterval(async () => {
    const el = document.getElementById('liveLine'); if (!el) return clearInterval(liveTimer);
    try { const s = (await (await fetch(`/api/venue/${venue}/state`)).json()).state as MatchState | null; if (s) el.innerHTML = line(s); else { clearInterval(liveTimer); show(); } } catch { /* try again next time */ }
  }, 10_000);
}

/** Demo clubs only: put everything back how it started, ready for the next sales call. */
function demoCard() {
  if (!DEMO_CLUBS.has(venue)) return '';
  return `<section class="card demo" id="demoBox"><h2>Demo club</h2>
    <p>${esc(club?.name ?? 'This club')} is made up, for showing Matchlight to other clubs. After a demo, reset it: any match is ended, Studio changes are undone and the opponents list goes back to the league.</p>
    <div><button class="danger" id="resetBtn">Reset the demo</button></div></section>`;
}
function wireDemo() {
  const b = document.getElementById('resetBtn') as HTMLButtonElement | null;
  if (!b) return;
  b.onclick = async () => {
    if (b.dataset.sure !== '1') { b.dataset.sure = '1'; b.textContent = 'Reset everything? Click again'; return; }
    b.disabled = true; b.textContent = 'Resetting…';
    const r = await api('reset', { method: 'POST' });
    if (!r.ok) { b.disabled = false; b.textContent = 'Reset the demo'; b.dataset.sure = ''; return alertBox((await r.json().catch(() => ({}))).error ?? 'Could not reset the demo'); }
    clearInterval(liveTimer);
    club = await loadClub(venue);
    await show();
    const box = document.getElementById('demoBox');
    if (box) box.insertAdjacentHTML('beforeend', '<p class="note ok">Reset. Ready for the next demo.</p>');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
}
function alertBox(msg: string) {
  const box = document.getElementById('demoBox');
  box?.insertAdjacentHTML('beforeend', `<p class="err">${esc(msg)}</p>`);
}

function showErr(msg: string) {
  const p = document.getElementById('startErr');
  if (p) { p.textContent = msg; p.hidden = false; }
}

mountHelp({ page: 'setup', venue, code: () => code, context: () => {
  const h = document.querySelector('#app h2')?.textContent ?? '';
  if (/in progress|full-time/i.test(h)) return `A match is on: ${document.getElementById('liveLine')?.textContent?.trim() ?? h}. The setup form is hidden until it is ended.`;
  if (document.getElementById('new')) return 'No match is on; the "Start a match" form is showing.';
  return 'They have not signed in with the admin code yet.';
} });

loadClub(venue).then(c => { club = c; brandHeader(c); return code ? show() : signIn(); })
  .catch(() => { app.innerHTML = `<section class="card"><h2>No club called “${esc(venue)}”</h2></section>`; });
